import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { colors, shadows, setColorScheme } from '../theme/colors';
import { designTokens } from '../theme/design-system';
import { useAppStore, selectThemeMode } from '../store/appStore';

interface ThemeContextType {
  colors: typeof colors;
  shadows: typeof shadows;
  tokens: typeof designTokens;
  themeMode: 'light' | 'dark';
  /** Persists the new mode and applies it immediately — every screen using
   * useThemedStyles() (src/hooks/useThemedStyles.ts) recomputes its styles
   * against the newly-mutated colors/shadows on its next render, triggered
   * by this same themeMode value changing. No reload needed anymore — see
   * useThemedStyles.ts's own doc comment for why that used to be required. */
  setThemeMode: (mode: 'light' | 'dark') => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const themeMode = useAppStore(selectThemeMode);
  const setStoredThemeMode = useAppStore((s) => s.setThemeMode);

  // colors.ts already boots with the correct palette (it reads the persisted
  // preference synchronously at module-load time, on web via localStorage —
  // see readPersistedThemeModeSync there for why that's necessary: every
  // StyleSheet.create() call across the app runs during module import, well
  // before this component ever renders, so this call here is just a defensive
  // no-op resync for the common case, not what makes the initial paint correct.
  setColorScheme(themeMode);

  // Web: browser-drawn controls (native date/time pickers, <select>
  // dropdowns, scrollbars, autofill) follow CSS color-scheme, not the app's
  // palette — without this they stay light in dark mode (or vice versa).
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.style.colorScheme = themeMode;
    }
  }, [themeMode]);

  const setThemeMode = (mode: 'light' | 'dark') => {
    setStoredThemeMode(mode);
    setColorScheme(mode);
  };

  const value = useMemo<ThemeContextType>(
    () => ({ colors, shadows, tokens: designTokens, themeMode, setThemeMode }),
    [themeMode]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

const defaultThemeContext: ThemeContextType = {
  colors,
  shadows,
  tokens: designTokens,
  themeMode: 'dark',
  setThemeMode: () => {},
};

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  return ctx ?? defaultThemeContext;
}
